import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Copy, Eye, Italic, Link as LinkIcon, Image as ImageIcon, List, Plus, Quote, Redo2, Star, StarOff, Trash2, Type, Undo2, X } from "lucide-react";
import { api } from "@/lib/api";
import { inputCls } from "./shared";

const mono = "font-mono text-[10px] uppercase tracking-[0.15em]";
const btn = "border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent disabled:opacity-50";
const dangerBtn = "border border-red-500/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 transition-colors duration-300 hover:bg-red-500/10 disabled:opacity-50";

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const blankPackage = {
  cover_image: "", main_caption: "", short_caption: "",
  story_cards: Array.from({ length: 5 }, () => ({ headline: "", description: "" })),
  cta_card: "", carousel_copy: "", story_copy: "", x_copy: "", reel_copy: "",
};

const blankOriginal = {
  title: "", subtitle: "", body: "", category: "Latest", series: "", tagsText: "", author: "FORE Staff",
  status: "Draft", scheduledLocal: "", featured: false, hero_image: "",
  sources: [], seo_title: "", seo_description: "", social_title: "", social_description: "", social_image: "",
  social_package: blankPackage,
};

const isoToLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const copyText = async (text, label = "Copied.") => {
  try {
    await navigator.clipboard.writeText(text || "");
    toast.success(label);
  } catch {
    toast.error("Copy failed — select and copy manually.");
  }
};

const toEditForm = (o) => ({
  title: o.title || "",
  subtitle: o.subtitle || o.excerpt || "",
  body: o.body || "",
  category: o.category || "Latest",
  series: o.series || "",
  tagsText: (o.tags || []).join(", "),
  author: o.author || "FORE Staff",
  status: o.status || "Draft",
  scheduledLocal: isoToLocal(o.scheduled_at),
  featured: !!o.featured,
  hero_image: o.hero_image || "",
  sources: (o.sources || []).map((s) => ({ ...s })),
  seo_title: o.seo_title || "",
  seo_description: o.seo_description || "",
  social_title: o.social_title || "",
  social_description: o.social_description || "",
  social_image: o.social_image || "",
  social_package: { ...blankPackage, ...(o.social_package || {}), story_cards: [...(o.social_package?.story_cards || []), ...blankPackage.story_cards].slice(0, 5) },
});

const toPayload = (form) => ({
  title: form.title,
  subtitle: form.subtitle,
  body: form.body,
  category: form.category,
  series: form.series,
  tags: form.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
  author: form.author,
  status: form.status,
  scheduled_at: form.status === "Scheduled" && form.scheduledLocal ? new Date(form.scheduledLocal).toISOString() : null,
  featured: form.featured,
  hero_image: form.hero_image,
  sources: form.sources,
  seo_title: form.seo_title,
  seo_description: form.seo_description,
  social_title: form.social_title,
  social_description: form.social_description,
  social_image: form.social_image,
  social_package: form.series.trim().toLowerCase() === "fore weekly" ? form.social_package : null,
});

/* ---------------- Rich text editor (bold / headings / links / lists / quote / image) ---------------- */
const RichEditor = ({ value, onChange, onImage, testid }) => {
  const ref = useRef(null);
  const focused = useRef(false);

  useEffect(() => {
    if (ref.current && !focused.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current.innerHTML);
  };
  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (url && /^https?:\/\//.test(url)) exec("createLink", url);
    else if (url) toast.error("Links must start with http:// or https://");
  };

  const tools = [
    { id: "bold", Icon: Bold, run: () => exec("bold"), label: "Bold" },
    { id: "italic", Icon: Italic, run: () => exec("italic"), label: "Italic" },
    { id: "h2", Icon: Type, run: () => exec("formatBlock", "h2"), label: "Heading" },
    { id: "h3", run: () => exec("formatBlock", "h3"), label: "Subheading", text: "H3" },
    { id: "quote", Icon: Quote, run: () => exec("formatBlock", "blockquote"), label: "Quote" },
    { id: "list", Icon: List, run: () => exec("insertUnorderedList"), label: "Bullet list" },
    { id: "link", Icon: LinkIcon, run: addLink, label: "Add link" },
    { id: "image", Icon: ImageIcon, run: onImage, label: "Insert FORE-owned image" },
    { id: "undo", Icon: Undo2, run: () => exec("undo"), label: "Undo" },
    { id: "redo", Icon: Redo2, run: () => exec("redo"), label: "Redo" },
  ];

  return (
    <div className="border border-white/20" data-testid={testid}>
      <div className="flex flex-wrap gap-1 border-b border-white/15 bg-black/40 p-2">
        {tools.map(({ id, Icon, run, label, text }) => (
          <button
            key={id}
            type="button"
            onClick={run}
            title={label}
            aria-label={label}
            data-testid={`${testid}-tool-${id}`}
            className="grid h-8 w-8 place-items-center text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-brand-accent"
          >
            {Icon ? <Icon className="h-3.5 w-3.5" /> : <span className="font-mono text-[10px] font-bold">{text}</span>}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current.innerHTML)}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; }}
        className="admin-rte min-h-[260px] bg-black/40 px-4 py-3 text-sm leading-relaxed text-white outline-none"
        data-testid={`${testid}-area`}
      />
    </div>
  );
};

/* ---------------- Media picker/upload field ---------------- */
const MediaField = ({ label, value, onChange, auth, onAuthFail, testid }) => {
  const [library, setLibrary] = useState([]);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);

  const loadLibrary = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/media", auth);
      setLibrary(res.data.media);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (open) loadLibrary(); }, [open, loadLibrary]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/news/admin/media", fd, auth);
      onChange(res.data.media.id);
      toast.success("Image uploaded to the FORE media library.");
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
      else toast.error(e?.response?.data?.detail || "Upload failed.");
    }
  };

  return (
    <div data-testid={testid}>
      <p className={`${mono} mb-2 text-white/50`}>{label}</p>
      {value ? (
        <div className="flex items-center gap-4 border border-white/15 bg-black/30 p-3">
          <img src={`/api/media/${value}`} alt={label} className="h-16 w-auto border border-white/10" />
          <div className="flex gap-2">
            <button type="button" onClick={() => { setOpen(true); loadLibrary(); }} className={btn} data-testid={`${testid}-change`}>Change</button>
            <button type="button" onClick={() => onChange("")} className={dangerBtn} data-testid={`${testid}-remove`}>Remove</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} className={btn} data-testid={`${testid}-upload`}>Upload Image</button>
          <button type="button" onClick={() => { setOpen(true); loadLibrary(); }} className={btn} data-testid={`${testid}-choose`}>Media Library</button>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid={`${testid}-file`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      {open && (
        <div className="mt-3 border border-white/15 bg-ink p-3" data-testid={`${testid}-library`}>
          <div className="mb-2 flex items-center justify-between">
            <p className={`${mono} text-white/40`}>FORE media library — owned/licensed images only</p>
            <button type="button" onClick={() => setOpen(false)} className="text-white/50 hover:text-white" aria-label="Close library"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid max-h-56 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
            {library.map((m) => (
              <button key={m.id} type="button" onClick={() => { onChange(m.id); setOpen(false); }} data-testid={`${testid}-pick-${m.id}`} className="group border border-white/10 p-1 hover:border-brand-accent">
                <img src={m.url} alt={m.filename} className="h-16 w-full object-cover" />
                <span className={`${mono} mt-1 block truncate text-white/40`}>{m.filename}</span>
              </button>
            ))}
            {library.length === 0 && <p className={`${mono} col-span-full py-6 text-center text-white/40`}>Library empty — upload an image first.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Copy-ready text field (social package) ---------------- */
const CopyField = ({ label, value, onChange, rows = 3, testid, hint }) => (
  <div data-testid={testid}>
    <div className="mb-1.5 flex items-center justify-between gap-3">
      <p className={`${mono} text-white/50`}>{label}</p>
      <button type="button" onClick={() => copyText(value, `${label} copied.`)} className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-brand-accent hover:text-white" data-testid={`${testid}-copy`}>
        <Copy className="h-3 w-3" /> Copy
      </button>
    </div>
    <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-input`} className={`${inputCls} w-full resize-y`} placeholder={hint || ""} />
  </div>
);

/* ---------------- Article preview (light editorial look) ---------------- */
const ArticlePreview = ({ form }) => (
  <div className="border border-white/15 bg-[#FBFAF7] p-6 text-neutral-900 sm:p-10" data-testid="original-preview">
    <div className="flex flex-col items-center">
      <img src="/logo-editorial.png" alt="Fore the Hell of It" className="h-14 w-auto" />
      <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.35em] text-neutral-500">Fore Originals</p>
    </div>
    <div className="mt-8 flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em]">
      <span className="bg-neutral-900 px-2 py-0.5 font-bold text-white">FORE Original</span>
      {form.series && <span className="border border-[#E4572E] px-2 py-0.5 font-bold text-[#E4572E]">{form.series}</span>}
    </div>
    {form.series.trim().toLowerCase() === "fore weekly" && (
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">5 Things That Mattered in Golf</p>
    )}
    <h3 className="mt-4 font-display text-3xl font-black leading-[1.02] tracking-tight">{form.title || "Untitled"}</h3>
    {form.subtitle && <p className="mt-3 text-base leading-relaxed text-neutral-600">{form.subtitle}</p>}
    <p className="mt-4 border-y border-neutral-200 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
      By {form.author || "FORE Staff"} · {form.status === "Scheduled" && form.scheduledLocal ? fmtTs(new Date(form.scheduledLocal).toISOString()) : fmtTs(new Date().toISOString())}
    </p>
    {form.hero_image && <img src={`/api/media/${form.hero_image}`} alt="Hero" className="mt-6 w-full" />}
    <div className="article-body mt-6 text-[1rem] leading-[1.8] text-neutral-800" dangerouslySetInnerHTML={{ __html: form.body }} />
    {form.sources.filter((s) => s.url).length > 0 && (
      <div className="mt-8 border-t border-neutral-200 pt-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">Sources &amp; References</p>
        <ul className="mt-3 space-y-1.5">
          {form.sources.filter((s) => s.url).map((s, i) => (
            <li key={i} className="text-sm text-neutral-700 underline decoration-neutral-300 underline-offset-4">{s.label || s.url}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = { Draft: "bg-white/10 text-white/60", Scheduled: "bg-yellow-500/15 text-yellow-400", Published: "bg-brand-accent/15 text-brand-accent" };
  return <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${map[status] || map.Draft}`} data-testid={`original-status-${status}`}>{status}</span>;
};

/* ---------------- FORE Originals manager ---------------- */
const Originals = ({ auth, onAuthFail, categories = [] }) => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blankOriginal);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const bodyImageRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/news/admin/originals", auth);
      setItems(res.data.originals);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (o) => { setForm(toEditForm(o)); setShowPreview(false); setModal({ mode: "edit", item: o }); };
  const openNew = () => { setForm(blankOriginal); setShowPreview(false); setModal({ mode: "new" }); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (modal.mode === "new") await api.post("/news/admin/originals", payload, auth);
      else await api.patch(`/news/admin/originals/${modal.item.id}`, payload, auth);
      toast.success(form.status === "Published" ? "Published." : form.status === "Scheduled" ? "Scheduled." : "Draft saved.");
      setModal(null);
      load();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error(e2?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const quickStatus = async (o, status) => {
    try {
      await api.patch(`/news/admin/originals/${o.id}`, toPayload({ ...toEditForm(o), status }), auth);
      toast.success(status === "Published" ? "Published — live on /news." : "Unpublished — moved back to drafts.");
      load();
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
      else toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  const toggleFeatured = async (o) => {
    try {
      await api.patch(`/news/admin/originals/${o.id}`, toPayload({ ...toEditForm(o), featured: !o.featured }), auth);
      toast.success(o.featured ? "Unfeatured." : "Featured at the top of FORE Originals.");
      load();
    } catch {
      toast.error("Action failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FORE Original permanently?")) return;
    await api.delete(`/news/admin/originals/${id}`, auth);
    toast.success("Deleted.");
    load();
  };

  const insertBodyImage = () => bodyImageRef.current?.click();
  const onBodyImagePicked = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/news/admin/media", fd, auth);
      document.execCommand("insertHTML", false, `<img src="${res.data.media.url}" alt="">`);
      toast.success("Image inserted into the article.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    }
  };

  const isWeekly = form.series.trim().toLowerCase() === "fore weekly";
  const setPkg = (patch) => setForm((f) => ({ ...f, social_package: { ...f.social_package, ...patch } }));
  const setCard = (i, patch) => setForm((f) => {
    const cards = f.social_package.story_cards.map((c, j) => (j === i ? { ...c, ...patch } : c));
    return { ...f, social_package: { ...f.social_package, story_cards: cards } };
  });

  return (
    <div data-testid="news-originals-admin">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className={`${mono} text-white/40`}>{items.length} original{items.length === 1 ? "" : "s"} — FORE-created editorial only, never rewritten wire items</p>
        <button onClick={openNew} className="flex items-center gap-2 bg-white px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-brand-accent" data-testid="original-new">
          <Plus className="h-3.5 w-3.5" /> New Original
        </button>
      </div>

      <div className="flex flex-col gap-3" data-testid="originals-list">
        {items.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-ink-2 p-4" data-testid={`admin-original-${o.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-display text-base font-bold tracking-tight text-white">{o.title}</p>
                <StatusBadge status={o.status} />
                {o.series && <span className="border border-[#E4572E]/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#E4572E]">{o.series}</span>}
              </div>
              <p className={`mt-1.5 ${mono} text-muted`}>
                {o.author} · {o.category}
                {o.status === "Scheduled" ? ` · goes live ${fmtTs(o.scheduled_at)}` : o.published_at ? ` · ${fmtTs(o.published_at)}` : ""}
                {o.featured ? " · Featured" : ""}
              </p>
              <p className={`mt-1 ${mono} text-white/30`}>/news/{o.slug}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {o.status === "Published" ? (
                <>
                  <a href={`/news/${o.slug}`} target="_blank" rel="noopener noreferrer" className={btn} data-testid={`original-view-${o.id}`}>View</a>
                  <button onClick={() => quickStatus(o, "Draft")} className={btn} data-testid={`original-unpublish-${o.id}`}>Unpublish</button>
                </>
              ) : (
                <button onClick={() => quickStatus(o, "Published")} className={btn} data-testid={`original-publish-${o.id}`}>Publish</button>
              )}
              <button onClick={() => toggleFeatured(o)} className={btn} data-testid={`original-feature-${o.id}`} title="Feature">
                {o.featured ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
              </button>
              <button onClick={() => openEdit(o)} className={btn} data-testid={`original-edit-${o.id}`}>Edit</button>
              <button onClick={() => remove(o.id)} className={dangerBtn} data-testid={`original-delete-${o.id}`}>Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className={`${mono} py-10 text-center text-white/40`}>No originals yet — publish real FORE journalism here.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/70 p-4 backdrop-blur-sm sm:p-8" onClick={() => setModal(null)} data-testid="original-modal">
          <form data-lenis-prevent onSubmit={save} onClick={(e) => e.stopPropagation()} className="max-h-[92vh] w-full max-w-4xl overflow-y-auto overscroll-contain border border-white/15 bg-ink-2 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-accent">{modal.mode === "new" ? "New FORE Original" : "Edit FORE Original"}</p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setShowPreview((v) => !v)} className={btn} data-testid="original-preview-toggle">
                  <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> {showPreview ? "Edit" : "Preview"}</span>
                </button>
                <button type="button" onClick={() => setModal(null)} className="text-white/50 hover:text-white" aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
            </div>

            {showPreview ? (
              <div className="mt-6"><ArticlePreview form={form} /></div>
            ) : (
              <div className="mt-6 flex flex-col gap-6">
                {/* Content */}
                <div className="flex flex-col gap-4">
                  <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Article headline *" data-testid="original-field-title" className={`${inputCls} w-full`} />
                  <input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="Subtitle / deck (shown under the headline)" data-testid="original-field-subtitle" className={`${inputCls} w-full`} />
                  <RichEditor value={form.body} onChange={(html) => setForm((f) => ({ ...f, body: html }))} onImage={insertBodyImage} testid="original-field-body" />
                  <input ref={bodyImageRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="original-body-image-file" onChange={(e) => { onBodyImagePicked(e.target.files?.[0]); e.target.value = ""; }} />
                  <p className={`${mono} text-white/30`}>Body images must be FORE-owned or licensed — uploads go to the FORE media library.</p>
                </div>

                {/* Classification */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className={`${mono} mb-2 text-white/50`}>Series</p>
                    <select value={form.series} onChange={(e) => setForm((f) => ({ ...f, series: e.target.value }))} data-testid="original-field-series" className={`${inputCls} w-full appearance-none`}>
                      <option value="">None</option>
                      <option value="FORE Weekly">FORE Weekly</option>
                    </select>
                  </div>
                  <div>
                    <p className={`${mono} mb-2 text-white/50`}>Category</p>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="original-field-category" className={`${inputCls} w-full appearance-none`}>
                      {(categories.length ? categories : [{ slug: "Latest", label: "Latest" }]).map((c) => (
                        <option key={c.slug} value={c.label}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className={`${mono} mb-2 text-white/50`}>Author</p>
                    <input value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} data-testid="original-field-author" className={`${inputCls} w-full`} />
                  </div>
                  <div>
                    <p className={`${mono} mb-2 text-white/50`}>Tags (comma separated)</p>
                    <input value={form.tagsText} onChange={(e) => setForm((f) => ({ ...f, tagsText: e.target.value }))} placeholder="weekly, majors, pga" data-testid="original-field-tags" className={`${inputCls} w-full`} />
                  </div>
                </div>

                <MediaField label="Hero image (FORE-owned / licensed)" value={form.hero_image} onChange={(v) => setForm((f) => ({ ...f, hero_image: v }))} auth={auth} onAuthFail={onAuthFail} testid="original-hero" />

                {/* Sources */}
                <div data-testid="original-sources">
                  <div className="mb-2 flex items-center justify-between">
                    <p className={`${mono} text-white/50`}>Sources &amp; references (links only)</p>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, sources: [...f.sources, { label: "", url: "" }] }))} className={btn} data-testid="original-source-add">Add Source</button>
                  </div>
                  {form.sources.map((s, i) => (
                    <div key={i} className="mb-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                      <input value={s.label} onChange={(e) => setForm((f) => ({ ...f, sources: f.sources.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) }))} placeholder="Label" data-testid={`original-source-label-${i}`} className={inputCls} />
                      <input value={s.url} onChange={(e) => setForm((f) => ({ ...f, sources: f.sources.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) }))} placeholder="https://…" data-testid={`original-source-url-${i}`} className={inputCls} />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, sources: f.sources.filter((_, j) => j !== i) }))} className={dangerBtn} data-testid={`original-source-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>

                {/* Publishing */}
                <div className="border border-white/10 p-4" data-testid="original-publishing">
                  <p className={`${mono} mb-3 text-white/50`}>Publishing</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {["Draft", "Scheduled", "Published"].map((s) => (
                      <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, status: s }))} data-testid={`original-status-${s.toLowerCase()}`}
                        className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${form.status === s ? "border-brand-accent bg-brand-accent text-ink" : "border-white/15 text-muted hover:border-brand-accent hover:text-brand-accent"}`}>
                        {s}
                      </button>
                    ))}
                    {form.status === "Scheduled" && (
                      <input type="datetime-local" value={form.scheduledLocal} onChange={(e) => setForm((f) => ({ ...f, scheduledLocal: e.target.value }))} data-testid="original-field-schedule" className={inputCls} />
                    )}
                  </div>
                  <label className="mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="original-field-featured" className="h-4 w-4 accent-[#CCFF00]" />
                    Featured (top of FORE Originals + homepage)
                  </label>
                </div>

                {/* SEO + Social */}
                <div className="border border-white/10 p-4" data-testid="original-seo">
                  <p className={`${mono} mb-3 text-white/50`}>SEO &amp; social sharing</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input value={form.seo_title} onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))} placeholder="SEO title (defaults to headline)" data-testid="original-field-seo-title" className={inputCls} />
                    <input value={form.social_title} onChange={(e) => setForm((f) => ({ ...f, social_title: e.target.value }))} placeholder="Social title" data-testid="original-field-social-title" className={inputCls} />
                    <textarea rows={2} value={form.seo_description} onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))} placeholder="Meta description (defaults to subtitle)" data-testid="original-field-seo-desc" className={`${inputCls} resize-y`} />
                    <textarea rows={2} value={form.social_description} onChange={(e) => setForm((f) => ({ ...f, social_description: e.target.value }))} placeholder="Social description" data-testid="original-field-social-desc" className={`${inputCls} resize-y`} />
                  </div>
                  <div className="mt-4">
                    <MediaField label="Social share image (optional — defaults to hero)" value={form.social_image} onChange={(v) => setForm((f) => ({ ...f, social_image: v }))} auth={auth} onAuthFail={onAuthFail} testid="original-social-image" />
                  </div>
                </div>

                {/* FORE Weekly social package */}
                {isWeekly && (
                  <div className="border border-[#E4572E]/40 p-4" data-testid="original-social-package">
                    <p className={`${mono} text-[#E4572E]`}>FORE Weekly — Social Package</p>
                    <p className={`${mono} mt-1 text-white/30`}>Copy captions and use FORE-owned graphics for manual posting. Direct social publishing is not connected.</p>
                    <div className="mt-4 flex flex-col gap-4">
                      <MediaField label="Cover graphic (FORE-owned design)" value={form.social_package.cover_image} onChange={(v) => setPkg({ cover_image: v })} auth={auth} onAuthFail={onAuthFail} testid="weekly-cover" />
                      <CopyField label="Main social caption" value={form.social_package.main_caption} onChange={(v) => setPkg({ main_caption: v })} testid="weekly-main-caption" />
                      <CopyField label="Short social caption" value={form.social_package.short_caption} onChange={(v) => setPkg({ short_caption: v })} rows={2} testid="weekly-short-caption" />
                      <div>
                        <p className={`${mono} mb-2 text-white/50`}>Story cards — slides 2–6</p>
                        {form.social_package.story_cards.map((c, i) => (
                          <div key={i} className="mb-3 border border-white/10 p-3" data-testid={`weekly-card-${i + 1}`}>
                            <div className="mb-2 flex items-center justify-between">
                              <p className={`${mono} text-white/40`}>Story {i + 1}</p>
                              <button type="button" onClick={() => copyText(`${c.headline}\n\n${c.description}`, `Story ${i + 1} copied.`)} className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-brand-accent hover:text-white" data-testid={`weekly-card-${i + 1}-copy`}>
                                <Copy className="h-3 w-3" /> Copy
                              </button>
                            </div>
                            <input value={c.headline} onChange={(e) => setCard(i, { headline: e.target.value })} placeholder="Story headline" data-testid={`weekly-card-${i + 1}-headline`} className={`${inputCls} mb-2 w-full`} />
                            <textarea rows={2} value={c.description} onChange={(e) => setCard(i, { description: e.target.value })} placeholder="Short story-card description" data-testid={`weekly-card-${i + 1}-description`} className={`${inputCls} w-full resize-y`} />
                          </div>
                        ))}
                      </div>
                      <CopyField label="Final CTA card" value={form.social_package.cta_card} onChange={(v) => setPkg({ cta_card: v })} rows={2} hint="Read the full FORE Weekly — forethehellofitmedia.com" testid="weekly-cta" />
                      <CopyField label="Instagram/Facebook carousel copy" value={form.social_package.carousel_copy} onChange={(v) => setPkg({ carousel_copy: v })} testid="weekly-carousel" />
                      <CopyField label="Instagram Story copy" value={form.social_package.story_copy} onChange={(v) => setPkg({ story_copy: v })} rows={2} testid="weekly-story" />
                      <CopyField label="X post copy" value={form.social_package.x_copy} onChange={(v) => setPkg({ x_copy: v })} rows={2} testid="weekly-x" />
                      <CopyField label="TikTok/Reel caption" value={form.social_package.reel_copy} onChange={(v) => setPkg({ reel_copy: v })} rows={2} testid="weekly-reel" />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button type="submit" disabled={busy} className="bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white disabled:opacity-60" data-testid="original-save">
                {busy ? "Saving…" : form.status === "Published" ? "Save & Publish" : form.status === "Scheduled" ? "Schedule" : "Save Draft"}
              </button>
              <button type="button" onClick={() => setModal(null)} className={btn}>Cancel</button>
              {modal.item?.slug && (
                <button type="button" onClick={() => copyText(`${window.location.origin}/news/${modal.item.slug}`, "Article link copied.")} className={`${btn} flex items-center gap-1.5`} data-testid="original-copy-link">
                  <Copy className="h-3 w-3" /> Copy Article Link
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Originals;
